/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2013, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 * VisRenderSupport
 * INTENT: VisPropertySupport - implements the portion of the VisAPI that is focused on properties and the property manager.
 */

define([
	'underscore',
	'../../../../data/VisPropertiesCSSProxy',
	'../../../../lib/@waca/core-client/js/core-client/ui/core/Events'
], function(
	_,
	VisPropertiesCSSProxy,
	Events
) {
	'use strict';

	const STORYTELLING_EFFECT_DURATION = 350;

	var VisPropertySupport = Events.extend({

		init: function(attributes) {
			//Initialize a minimal set of attributes (nested collections from spec etc)
			VisPropertySupport.inherited('init', this, arguments);
			//Assign all attributes to members of this object (to keep old behaviour.)
			this.properties = attributes.properties;
			this.visModelManager = attributes.visModelManager;
			this.dashboardApi = attributes.dashboardApi;
			this.content = attributes.content;
		},

		remove: function() {
			// cleanup external events if needed
		},

		getProperties: function() {
			return this.properties;
		},
		getDefinition: function() {
			return this.visModelManager.getDefinition();
		},

		/**
		 * Try to merge new properties with existing ones
		 * keep the values from the properties with same id and type
		 * create a mapping of current viz properties to their respective values
		 * @param definition: properties of the new vis definition as defined in the smart-vis service
		 *                   (see: smart-viz/ws/src/main/webapp/WEB-INF/localGallery)
		 */
		buildProperties: function(propDefs, options) {
			let propertiesDefs = propDefs;
			if(this.dashboardApi.getType() === 'story'){
				propertiesDefs = this._initializeEntryDurationForStory(propDefs);
			}
			this.getProperties().applyDefaults(propertiesDefs, options);
		},
		/**
		 * Overwrites the property default entry animation to STORYTELLING_EFFECT_DURATION
		 * Because storytelling needs a minimum entry animation duration for it to be noticeable while revealing the widget
		 * @param {Object} propertiesDefs properties of the new vis definition as defined in the smart-vis service
		 * @returns {Object} - the modified properties
		 */
		_initializeEntryDurationForStory: function(propertiesDefs = []) {
			return propertiesDefs.map( propDef => {
				const isEntryDurationID = propDef.id && propDef.id.indexOf('effect.entry') >=0 && propDef.id.indexOf('duration') >=0;
				if(isEntryDurationID && propDef.defaultValue < STORYTELLING_EFFECT_DURATION){
					let propertyDefinition = { ...propDef };
					propertyDefinition.defaultValue = STORYTELLING_EFFECT_DURATION;
					return propertyDefinition;
				}
				return propDef;
			});
		},

		/**
		 * @returns a VisProperty by its id if it is defined for the current viz, null otherwise
		 */
		getPropertyById: function(id) {
			return this.getProperties().get(id);
		},

		/**
		 * @returns true if this property exists in the current vis property-to-value map
		 */
		currentVizHasProperty: function(id) {
			return this._currentPropertiesMap && this._currentPropertiesMap.hasOwnProperty(id);
		},


		/**
		 * @returns true if this property is a color class like 'color11'
		 */
		_isColorClass(colorClass){
			const pattern = new RegExp('color\\d+|grayShade\\d+');
			return pattern.test(colorClass);
		},


		_getEffectiveBackgroundColor() {
			let currentContent = this.content;
			let currentColor = currentContent.getPropertyValue('fillColor');
			while (currentColor === 'transparent'){
				// check the parent
				currentContent = currentContent.getContainer();
				if (!currentContent){
					break;
				}
				currentColor = currentContent.getPropertyValue('fillColor') || 'transparent';
			}

			return currentColor;
		},


		/**
		 * @returns the value of the named property as a string, number, boolean depending on the property type.
		 *	Note: This function only returns null if the property does not exist in this definition.
		 */
		getPropertyValue: function(id) {
			var visProperty = this.getPropertyById(id);
			let value = null;
			if (visProperty) {
				value = visProperty.getValue();

				const colorsService = this.dashboardApi.getFeature('Colors');

				// if the property value is a color class, we need to transform it into hex value before set in vida model
				if (visProperty.isResolveColor() && this._isColorClass(value)  && value != undefined) {
					value =  colorsService.getHexColorFromDashboardColorSet(value);
				}

				if (value == null){
					const mappedId = colorsService.getMappedId(id);
					if (mappedId) {
						// Check the proper color value from the theme based on the effective background color.
						const colorProps = colorsService.getForegroundColorPropertiesForUIElement(this._getEffectiveBackgroundColor());
						const colorProperty = colorProps.find((prop) => { return prop.id === mappedId;});
						if (colorProperty){
							value = colorProperty.value;
						}
					}
				}

				if (value == null){
					value = visProperty.getPredefinedValue();
					if (value == null) {
						// the order of checking theme value and default value has switched because VIDA has now added the properties to their definition with default values
						// TODO find the use case for this 
						value = this._getValueForPropertyOfUIElement(id);
						if (value == null) {
							value = visProperty.getDefaultValue();
						}
					}

				}
			}

			return value;
		},

		_getValueForPropertyOfUIElement: function (id) {
			const colorsService = this.dashboardApi.getFeature('Colors');
			// TODO: Should not need a reference to visModel
			const mappedId =  colorsService.getMappedId(id);
			// for property like colorPalette does not have a mappedId but can find value for property of UI Element by its id.
			const elementId = mappedId ? mappedId : id;
			return colorsService.getValueForPropertyOfUIElement('visualization', elementId);
		},


		getColorPalettePropertyValue: function() {
			return this.getPropertyValue('colorPalette');
		},
		getConditionalPalettePropertyValue: function() {
			return this.getPropertyValue('condColorPalette');
		},



		/**
		 * @returns a map of simple property values keyed by propertyId for fast lookup.
		 * See also getVisPropertiesAsMap
		 */
		getPropertiesAsMap: function() {
			return this._currentPropertiesMap;
		},

		/**
		 * @returns a map of VisProperty objects keyed by propertyId for fast lookup.
		 */
		getVisPropertiesAsMap: function() {
			return this.getProperties().modelMap || {};
		},

		/**
		 * A proxy object for accessing VisPropery values in a form compatible for applying values to CSS
		 */
		getVisPropertiesCSSProxy: function() {
			return new VisPropertiesCSSProxy({
				props: this.getVisPropertiesAsMap()
			});
		},

		_notifyViews: function(eventName, payload) {
			this.visModelManager.trigger(eventName, payload);
		}


	});

	return VisPropertySupport;
});
